import { useState } from "react";

import { Input, Text, Table, ErrorMsg } from "@fragments";

import {
    DirectionsRunRounded,
    FavoriteRounded,
    HubRounded,
    SportsEsportsRounded,
} from "@mui/icons-material";

import { ReactComponent as CoinIcon } from "@assets/draws/coin.svg";

import "./styles.scss";

const Coin = () => <CoinIcon width={14} height={14} />;

function PointsCell({ value, onChange, hasCoin = false }) {
    const [v, setV] = useState(value);

    function handleChange(input) {
        const numeric = String(input).replace(/\D/g, "");
        const parsed = parseInt(numeric, 10) || 0;
        setV(parsed);
        onChange(parsed);
    }

    return (
        <Input.Integer
            start={hasCoin ? <Coin /> : null}
            value={v}
            onInput={(e) => handleChange(e.target?.value ?? e)}
            onChange={(e) => handleChange(e.target?.value ?? e)}
            className="gamification-cell-input"
        />
    );
}

function SectionTitle({ icon, label }) {
    return (
        <span className="gamification-section-title">
            {icon}
            <Text.span weight={500}>{label}</Text.span>
        </span>
    );
}

function buildColumns({ sectionTitle, getPoints, setPoints, getDailyLimit, setDailyLimit }) {
    return [
        {
            id: "label",
            title: sectionTitle,
            minWidth: 164,
            allowReorder: false,
            customRender: (row) => <Text.span contrast={8}>{row.label}</Text.span>,
        },
        {
            id: "points",
            title: "Pontos",
            align: "center",
            minWidth: 120,
            allowReorder: false,
            customRender: (row) => (
                <PointsCell
                    hasCoin
                    value={getPoints(row.key)}
                    onChange={(v) => setPoints(row.key, v)}
                />
            ),
        },
        {
            id: "daily_limit",
            title: "Limite diário",
            align: "center",
            minWidth: 120,
            allowReorder: false,
            customRender: (row) => {
                if (!setDailyLimit) {
                    return <Text.span contrast={5}>-</Text.span>;
                }
                return (
                    <PointsCell
                        value={getDailyLimit(row.key)}
                        onChange={(v) => setDailyLimit(row.key, v)}
                    />
                );
            },
        },
    ];
}

export default function GamificationSettings({ configRef, errors = {} }) {
    const settings = configRef.current.gamification;

    if (!settings) {
        return (
            <div className="gamification-settings">
                <Text.h3
                    className="mb-2 mt-4"
                    value="Regras de pontuação"
                    left={<SportsEsportsRounded />}
                />
                <ErrorMsg>
                    {errors?.gamification || "Configure as regras de pontuação"}
                </ErrorMsg>
            </div>
        );
    }

    function updatePosts(rowKey, field, value) {
        const copy = { ...settings };
        copy.posts[rowKey][field] = value;
        configRef.current.gamification = copy;
    }

    function updateInteractions(rowKey, value) {
        const copy = { ...settings };
        copy.interactions[rowKey] = value;
        configRef.current.gamification = copy;
    }

    function updateActivity(field, value) {
        const copy = { ...settings };
        copy.activity[field] = value;
        configRef.current.gamification = copy;
    }

    const postsColumns = buildColumns({
        sectionTitle: (
            <SectionTitle icon={<HubRounded fontSize="small" />} label="Posts" />
        ),
        getPoints: (key) => settings.posts[key].points,
        setPoints: (key, v) => updatePosts(key, "points", v),
        getDailyLimit: (key) => settings.posts[key].daily_limit,
        setDailyLimit: (key, v) => updatePosts(key, "daily_limit", v),
    });

    const interactionsColumns = buildColumns({
        sectionTitle: (
            <SectionTitle
                icon={<FavoriteRounded fontSize="small" />}
                label="Interações"
            />
        ),
        getPoints: (key) => settings.interactions[key],
        setPoints: (key, v) => updateInteractions(key, v),
    });

    const activityColumns = buildColumns({
        sectionTitle: (
            <SectionTitle
                icon={<DirectionsRunRounded fontSize="small" />}
                label="Atividades"
            />
        ),
        getPoints: () => settings.activity.points,
        setPoints: (_, v) => updateActivity("points", v),
        getDailyLimit: () => settings.activity.daily_limit,
        setDailyLimit: (_, v) => updateActivity("daily_limit", v),
    });

    return (
        <div className="gamification-settings">
            <Text.h3
                className="mb-2 mt-4"
                value="Regras de pontuação"
                left={<SportsEsportsRounded />}
            />

            {errors?.gamification && <ErrorMsg>{errors.gamification}</ErrorMsg>}

            <Table
                id="group-gamification-posts"
                draggable={false}
                contained
                columns={postsColumns}
                data={[
                    { id: "with_photo", key: "with_photo", label: "Com foto" },
                    { id: "only_text", key: "only_text", label: "Apenas texto" },
                ]}
            />

            <Table
                id="group-gamification-interactions"
                draggable={false}
                contained
                className="mt-2"
                columns={interactionsColumns}
                data={[
                    { id: "like", key: "like", label: "Reações" },
                    { id: "comment", key: "comment", label: "Comentários" },
                ]}
            />

            <Table
                id="group-gamification-activity"
                draggable={false}
                contained
                className="mt-2"
                columns={activityColumns}
                data={[{ id: "activity", key: "activity", label: "Registro" }]}
            />
        </div>
    );
}
